import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import commentOnPage from '../../assets/icons/onPageCommentIconSmall.svg';
import ActionsMenu from '../../components/ActionsMenu/ActionsMenu.component';
import Layout from '../../components/Layout/Layout.component';
import Pagination from '../../components/Pagination.component';
import ProcessesIcon from '../../components/ProcessesList/ProcessIcon.component';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { DateFilter } from '../../components/SelectableTable/filters/DateFilter.component';
import { ItemsFilter } from '../../components/SelectableTable/filters/ItemsFilter.component';
import { RangeFilter } from '../../components/SelectableTable/filters/RangeFilter.component';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import { TableSelection } from '../../components/SelectableTable/SelectableTable.types';
import ViewContainer from '../../components/ViewContainer/ViewContainer.component';
import { useExaminer } from '../../Context/Examiner.context';
import { useLayoutContext } from '../../Context/Layout.context';
import useSessionUser from '../../hooks/useSessionUser';
import { AssessmentSessionProcessTypeId } from '../../services/exams/assessmentSession/assessments.types';
import { sendBackAssessedSolutions } from '../../services/scoring/scoring.service';
import { SendBackAssessedExamTaskGroupSolutionsResponse, SendBackResult } from '../../services/scoring/scoring.types';
import {
  exportExamTaskSampleSolutionsForExaminerApi,
  getExamTaskGroupSolutionScoreStats,
  sendbackAvailable,
} from '../../services/scoringModel/scoringModel.service';
import {
  AssessmentSessionCloseability,
  ExamTaskGroupSolutionScoreStatistic,
  ExamTaskGroupSolutionScoreStats,
  SendbackAvailable,
} from '../../services/scoringModel/scoringModel.types';
import { RootState } from '../../store';
import { getProcessLabel } from '../../utils/assessmentSessionProcess';
import { getAssessmentSampleAssessmentGroupSolutionUrl } from '../../utils/routerUrls';
import { HeaderCard } from '../Assessment/HeaderCard/HeaderCard.component';
import { formatDate } from '../Assessment/Helpers/FormatDate.component';
import { getProgress } from '../Assessment/Helpers/Progress.component';
import { ExaminerFunction, ExaminerSampleAssessmentGroupFunction } from './../Assessment/Assessment.types';
import SendbackRGZButton from './../Assessment/SendbackRGZButton/SendbackRGZButton.component';
import AcceptanceStatusChip from './AcceptanceStatusChip.component';
import { CoordinatorConfirmation } from './CoordinatorConfirmation/CoordinatorConfirmation.component';
import { useStyles } from './SampleAssessment.styles';
import {
  AcceptanceStatus,
  ExamTaskGroupSampleSolution,
  ExamTaskGroupSampleSolutionsTableItem,
  ProgressFilter,
  SampleAssessmentSendbackPrompt,
  SampleAssessmentSortBy,
} from './SampleAssessment.types';
import SampleAssessmentStatistics from './SampleAssessmentStatistics/SampleAssessmentStatistics.component';
import { ContextualHelp } from '../../components/ContextualHelp/ContextualHelp.component';
import { contextualHelpActions } from '../../reducers/contextualHelpReducer/contextualHelpSlice';
import OutlinedButton from '../../components/buttons/OutlinedButton.component';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { Chip, Grid, MenuItem, Typography } from '@material-ui/core';
import { exportFile } from '../../utils/servicesHelper';
import dIcon from '../../assets/icons/d-icon.svg';
import { NotificationsTableItem } from '../Assessment/Helpers/Notifications.component';
import TooltipOneComponent from '../../components/TooltipOne/TooltipOne.component';
import Checkbox from '../../components/Checkbox.component';
import { AdvancedFilterModal } from '../Assessment/Modals/AdvancedFilter/AdvancedFilter.component';
import { getSortByPropertyNameFromColumnId } from '../SampleAssessmentRepo/Helpers/functionsHelpers';
import { useSampleAssessmentColumns } from './useSampleAssessmentColumns';
import { useTranslation } from 'react-i18next';
import { useSampleAssessmentTableData } from './useSampleAssessmentTableData';
import { SampleAssessmentSessionState } from '../Dashboard/Dashboard.types';

const PROGRESS_BAR_ID = 10;
const TABLE_BAR_ID = 11;

const SampleAssessment: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);

  if (!assessmentSession) {
    return <Redirect to="/dashboard" />;
  }

  const { user: auth, userAssessmentSessionInfo } = useSessionUser();
  const userFunction = userAssessmentSessionInfo.function;
  const sampleAssessmentGroupFunction = userAssessmentSessionInfo.sampleAssessmentGroupFunction;

  const { showLoader, openAlert, isSidebarMinimized, setIsLoader } = useLayoutContext();
  const classes = useStyles();
  const { examiner } = useExaminer();
  const isContextualHelp = useSelector((state: RootState) => state.contextualHelp.isContextualHelp);

  const {
    filter,
    pageInfo,
    examTaskGroupSolutionsData,
    sortByColumnId,
    setSortByColumnId,
    isDescending,
    setIsDescending,
    advancedFilter,
    checkedTasks,
    setCheckedTasks,
    checkedCriterions,
    setCheckedCriterions,
    filterState,
    handleFilterChange,
    handleNoAnswerFilterChange,
    setIsResetAdvancedFilter,
    setAdvancedFilterModalOpen,
    disableChangesTillResponse,
    signal,
    refreshData,
    handleReload,
    getAssessment,
    isAdvancedFilter,
    uncheckFilterItem,
    uncheckAllFilters,
    advancedFilterModalOpen,
    isResetAdvancedFilter,
    handleAdvancedFilterChange,
    onAdvancedFilterReset,
    advancedFilterBody,
  } = useSampleAssessmentTableData({ examiner, assessmentSession, auth, userFunction });

  const [itemsSelection, setItemsSelection] = useState<TableSelection>({ items: [] });
  const [sampleAssessmentClosingStatus, setSampleAssessmentClosingStatus] = useState<
    AssessmentSessionCloseability | undefined
  >(undefined);
  const [examTaskGroupSolutions, setExamTaskGroupSolutions] = useState<ExamTaskGroupSampleSolutionsTableItem[]>([]);
  const [isTeamList, setIsTeamList] = useState<boolean>(false);
  const [statData, setStatData] = useState<ExamTaskGroupSolutionScoreStats | undefined>(undefined);
  const [isSendbackAvailable, setIsSendbackAvailable] = useState<boolean>(false);
  const [activatedContextualHelp, setActivatedContextualHelp] = useState<number | undefined>();

  const [sendbackPrompt, setSendbackPrompt] = useState<SampleAssessmentSendbackPrompt>({
    open: false,
    type: 'success',
  });

  useEffect(() => {
    if (sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.CZWO)
      sendbackAvailable().then(
        (result: SendbackAvailable) => {
          setIsSendbackAvailable(result.available);
        },
        () => {
          console.log('error');
        },
      );
  }, [examTaskGroupSolutionsData]);

  useEffect(() => {
    if (examiner && examiner.function !== undefined) {
      switch (examiner.function) {
        case ExaminerFunction.CZE:
        case ExaminerFunction.CZWO:
          setIsTeamList(false);
          break;
        default:
          setIsTeamList(true);
          break;
      }
    }
  }, [examiner]);

  const getExaminerName = () => {
    if (examiner && examiner.examiner) {
      return examiner.examiner.displayName;
    } else {
      return undefined;
    }
  };

  const { columns } = useSampleAssessmentColumns();

  const getProcessIcon = (orgz: ExamTaskGroupSampleSolution) => {
    const processInfo = getProcessLabel(orgz.processType ?? undefined);
    return <ProcessesIcon letter={processInfo.letter} tooltip={processInfo.label} toolTipPlacement="top" />;
  };

  useEffect(() => {
    if (examTaskGroupSolutionsData?.length && examiner) {
      const transformedData: ExamTaskGroupSampleSolutionsTableItem[] = examTaskGroupSolutionsData.map(
        (orgz: ExamTaskGroupSampleSolution) => ({
          id: orgz.examTaskGroupSolutionScoreId,
          lastName: `${orgz.examinerFirstName[0]}. ${orgz.examinerLastName}`,
          examTaskGroupSolutionScoreId: (
            <Link
              to={getAssessmentSampleAssessmentGroupSolutionUrl(
                orgz.schoolId,
                orgz.examTaskGroupSolutionId,
                orgz.examTaskGroupSolutionScoreId,
                orgz.examinerId,
                examiner.function.toString(),
              )}
            >
              {orgz.examTaskGroupSolutionScoreId}
            </Link>
          ),
          schoolId: <>{orgz.schoolId}</>,
          processType: orgz.processType !== null ? getProcessIcon(orgz) : <></>,
          pointSum: (
            <>
              {orgz.pointSum !== null ? orgz.pointSum.toString() : ''}
              {orgz.examTaskNumbersWithNoAnswer.length > 0 && (
                <TooltipOneComponent
                  title={`Brak odpowiedzi w zadaniu: ${orgz.examTaskNumbersWithNoAnswer.join(', ')}`}
                  placement="top"
                >
                  <Chip
                    style={{
                      color: '#000000',
                      borderColor: '#000000',
                      justifyContent: 'center',
                      marginLeft: '8px',
                    }}
                    key={'BO'}
                    label={'BO'}
                    variant="outlined"
                    size="small"
                  />
                </TooltipOneComponent>
              )}
            </>
          ),
          alerts: (
            <NotificationsTableItem
              isScoringAdjusted={orgz.isScoringAdjusted}
              isCommentOnCriteria={orgz.isCommentOnCriteria}
              isCommentOnImage={orgz.isCommentOnImage}
              isCommentOffPage={orgz.isCommentOffPage}
            />
          ),
          progress: getProgress(
            orgz.progress,
            0, //orgz.status, todo
            orgz.examinerId,
            [orgz.examTaskGroupSolutionScoreId],
            () => {
              console.log('sending back');
            },
            false,
          ),
          acceptanceStatus: <AcceptanceStatusChip status={orgz.acceptanceStatus} />,
          updateDate: orgz.updateDate ? formatDate(new Date(orgz.updateDate)) : <></>,
          actions: <></>
        }),
      );
      setExamTaskGroupSolutions(transformedData);
    } else {
      setExamTaskGroupSolutions([]);
    }
  }, [examTaskGroupSolutionsData, examiner]);

  const filterElements: { [key: string]: JSX.Element } = {
    processType: (
      <ItemsFilter
        valueName="processType"
        valueLabel="Proces"
        onChange={handleFilterChange}
        resetFilter={filter.processType === ''}
        itemsWithValues={[
          { label: 'Ćwiczeniowy', value: AssessmentSessionProcessTypeId.Training.toString() },
          { label: 'Kwalifikacyjny', value: AssessmentSessionProcessTypeId.Qualification.toString() },
          { label: 'Kontrolny', value: AssessmentSessionProcessTypeId.Control.toString() },
        ]}
        filter={filter.processType}
      />
    ),
    acceptanceStatus: (
      <ItemsFilter
        valueName="acceptanceStatus"
        valueLabel="Status"
        onChange={handleFilterChange}
        resetFilter={filter.acceptanceStatus === ''}
        transformHorizontal={40}
        transformVertical={-30}
        itemsWithValues={[
          { label: 'Odesłane przez CZWO', value: AcceptanceStatus.CzwoAssessed.toString() },
          { label: 'Zaakceptowane przez PZWO', value: AcceptanceStatus.PzwoAccepted.toString() },
          { label: 'Zaakceptowane przez KK', value: AcceptanceStatus.KkAccepted.toString() },
        ]}
        filter={filter.acceptanceStatus}
      />
    ),
    progress: (
      <ItemsFilter
        valueName="progress"
        valueLabel="Postęp"
        onChange={handleFilterChange}
        resetFilter={filter.progress === ''}
        itemsWithValues={[
          { label: '0-20%', value: ProgressFilter._0_20.toString() },
          { label: '20%-40%', value: ProgressFilter._20_40.toString() },
          { label: '40%-60%', value: ProgressFilter._40_60.toString() },
          { label: '60%-80%', value: ProgressFilter._60_80.toString() },
          { label: '80%-100%', value: ProgressFilter._80_100.toString() },
        ]}
        filter={filter.progress}
      />
    ),
    alerts: (
      <ItemsFilter
        valueName="alerts"
        valueLabel="Alerts"
        onChange={handleFilterChange}
        resetFilter={filter.alerts === ''}
        itemsWithImages={[
          {
            text: 'Komentarz do kryterium',
            value: 'isCommentOnCriteria',
            jsxElement: (
              <div className={classes.roundSmallBlue}>
                <SpeakerNotesIcon style={{ color: 'white', marginTop: '5px', width: '0.5em', height: '0.5em' }} />
              </div>
            ),
          },
          { text: 'Komentarz na stronie', value: 'isCommentOnImage', imagePath: `${commentOnPage}` },
          {
            text: 'Komentarz poza stroną',
            value: 'isCommentOffPage',
            jsxElement: (
              <div className={classes.roundSmallPink}>
                <RateReviewOutlinedIcon style={{ color: 'white', marginTop: '5px', width: '0.5em', height: '0.5em' }} />
              </div>
            ),
          },
          { text: 'Dostosowane zasady oceniania', value: 'isScoringAdjusted', imagePath: `${dIcon}` },
        ]}
        filter={filter.alerts}
      />
    ),
    pointSum: (
      <RangeFilter
        fromValueName="pointSumFrom"
        toValueName="pointSumTo"
        onChange={handleFilterChange}
        resetFilter={
          filter.pointSumFrom === '' && filter.pointSumTo === '' && filter.examTaskNumbersWithNoAnswer === ''
        }
        isNoAnswerFilter={!!filter.examTaskNumbersWithNoAnswer.length}
        isAdvancedFilter={advancedFilter !== null || checkedTasks.length > 0 || checkedCriterions.length > 0}
        onAdvancedFilterReset={onAdvancedFilterReset}
        noAnswerFilter={
          <MenuItem disableRipple>
            <Checkbox
              className={classes.filterMenuCheckbox}
              checked={!!filter.examTaskNumbersWithNoAnswer.length}
              onChange={handleNoAnswerFilterChange}
            />
            <Typography style={{ fontSize: '13px', paddingLeft: '6px' }}>Brak odpowiedzi</Typography>
          </MenuItem>
        }
        advancedFilter={
          <MenuItem disableRipple onClick={() => setAdvancedFilterModalOpen(true)}>
            <Typography style={{ fontSize: '13px', paddingLeft: '70px' }}>Filtrowanie zaawansowane</Typography>
          </MenuItem>
        }
      />
    ),
    updateDate: (
      <DateFilter
        fromValueName="updateDateFrom"
        toValueName="updateDateTo"
        onChange={handleFilterChange}
        resetFilter={filter.updateDateFrom === '' && filter.updateDateTo === ''}
        isActiveFilter={filter.updateDateFrom !== '' || filter.updateDateTo !== ''}
        valueFrom={filter.updateDateFrom as string}
        valueTo={filter.updateDateTo as string}
      />
    ),
  };

  useEffect(() => {
    getExamTaskGroupSolutionScoreStats()
      .then((stats: AxiosResponse<ExamTaskGroupSolutionScoreStats>) => {
        const filteredStatsList = stats.data;
        setSampleAssessmentClosingStatus(filteredStatsList.assessmentSessionCloseability);
        filteredStatsList.statisticList = filteredStatsList.statisticList.filter(
          (item: ExamTaskGroupSolutionScoreStatistic) => {
            return [
              AssessmentSessionProcessTypeId.Training,
              AssessmentSessionProcessTypeId.Qualification,
              AssessmentSessionProcessTypeId.Control,
            ].includes(item.processTypeId);
          },
        );
        setStatData(filteredStatsList);
      })
      .catch(() => {
        console.log('error');
      });
  }, [examTaskGroupSolutionsData]);

  const displayErrorPrompt = () => {
    setSendbackPrompt({
      open: true,
      type: 'error',
      mainText: 'Wysyłanie ocenionych RGZ nie powiodło się, spróbuj ponownie później.',
    });
  };

  const sendbackMultiple = () => {
    setIsLoader(true);
    sendBackAssessedSolutions([])
      .then(
        (result: AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>) => {
          setSendbackPrompt({
            open: true,
            type: 'success',
            mainText: `${
              result.data.results.filter((item) => item.sendBackResult === SendBackResult.Success).length
            } z ${result.data.results.length}`,
            subTexts: [{ text: 'Rozwiązania Grupy Zadań zostały pomyślnie odesłane.' }],
          });
          refreshData(true);
        },
        () => displayErrorPrompt(),
      )
      .catch(() => displayErrorPrompt())
      .finally(() => setIsLoader(false));
  };

  useEffect(() => {
    dispatch(contextualHelpActions.toogleContextualHelp(false));
  }, []);

  useEffect(() => {
    const statisticsBar = document.getElementById('statisticsBar');
    const contextualHelp10 = document.getElementById('contextualHelp-10');
    if (statisticsBar && contextualHelp10) {
      contextualHelp10.style.left = statisticsBar.offsetWidth - contextualHelp10.offsetWidth + 'px';
      contextualHelp10.style.top =
        statisticsBar.getBoundingClientRect().top - contextualHelp10.getBoundingClientRect().top + 'px';
    }

    const tableMenu = document.getElementById('tableMenu');
    const contextualHelp11 = document.getElementById('contextualHelp-11');
    if (tableMenu && contextualHelp11) {
      contextualHelp11.style.left =
        (tableMenu.getBoundingClientRect().right - tableMenu.getBoundingClientRect().left) / 2 + 'px';
    }
  }, [isContextualHelp]);

  const exportExamTaskSampleSolutionsForExaminer = () => {
    if (examiner && assessmentSession) {
      const filters: { [key: string]: string } = {};
      Object.keys(filter).forEach((key) => {
        if (Array.isArray(filter[key])) {
          filters[key] = (filter[key] as string[]).join(',') as string;
        } else {
          filters[key] = filter[key] as string;
        }
      });
      showLoader(true, 'Eksportuję dane');
      if (examiner.examinerFunctionId) disableChangesTillResponse();
      exportExamTaskSampleSolutionsForExaminerApi(
        assessmentSession.assessmentSessionName,
        signal ?? undefined,
        getSortByPropertyNameFromColumnId(sortByColumnId),
        isDescending,
        filters,
        advancedFilterBody,
      )
        .then(
          (response: Blob) => {
            exportFile(response, `ZWO/${assessmentSession.assessmentSessionName}`);
          },
          () => {
            openAlert('error', 'Eksport danych nie powiódł się');
          },
        )
        .finally(() => showLoader(false));
    }
  };

  const caption = isTeamList ? 'Wykaz kart zespołu wzorcowych ocen:' : 'Wykaz wyselekcjonowanych do oceny RGZ:';

  return (
    <React.Fragment>
      <Layout viewType="sampleExaminer">
        <Grid>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <OutlinedButton
              className={classes.exportButton}
              startIcon={<SaveAltIcon className={classes.exportButtonIcon} />}
              onClick={() => exportExamTaskSampleSolutionsForExaminer()}
            >
              Eksportuj
            </OutlinedButton>
          </div>
          <div>
            <ViewContainer isButton={true}>
              <HeaderCard
                examinerName={getExaminerName()}
                sessionName={`${assessmentSession.assessmentSessionName} / ${assessmentSession.examTaskGroupName}`}
                isTeamList={isTeamList}
                isExaminerBlocked={false}
                assessmentMode="sampleAssessment"
                processStatus={sampleAssessmentClosingStatus}
                onProcessClosed={() => handleReload()}
              />
              {isContextualHelp && (
                <div style={{ position: 'relative', left: '90%', top: '-20px' }}>
                  <ContextualHelp
                    id={PROGRESS_BAR_ID}
                    isActive={activatedContextualHelp === PROGRESS_BAR_ID}
                    onClick={setActivatedContextualHelp}
                    placement={'left'}
                  />
                </div>
              )}
              <SampleAssessmentStatistics statData={statData} />
              <div id="tableMenu" className={classes.menu}>
                {sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.CZWO && (
                  <SendbackRGZButton
                    buttonType="multiple"
                    isDisabled={!isSendbackAvailable}
                    sendbackFunction={sendbackMultiple}
                  />
                )}
                <div>
                  {userFunction === ExaminerFunction.KK && itemsSelection.items.length > 0 && (
                    <CoordinatorConfirmation
                      isVisible={true}
                      selection={itemsSelection}
                      totalItems={pageInfo.totalCount}
                      reload={handleReload}
                      IsSampleAssessmentSessionClosed={
                        assessmentSession.sampleAssessmentSessionStateId === SampleAssessmentSessionState.Closed
                      }
                      examTaskGroupSolutions={examTaskGroupSolutions}
                    />
                  )}
                </div>
                {isContextualHelp && (
                  <div style={{ position: 'relative', left: '50px' }}>
                    <ContextualHelp
                      id={TABLE_BAR_ID}
                      isActive={activatedContextualHelp === TABLE_BAR_ID}
                      onClick={setActivatedContextualHelp}
                      placement={'right'}
                    />
                  </div>
                )}
                <div className={classes.pagination}>
                  <Pagination pageInfo={pageInfo} getPage={getAssessment} />
                </div>
              </div>
              <div
                className={[
                  classes.sampleAssessmentTableContainerFlex,
                  isSidebarMinimized ? classes.sampleAssessmentTableContainerFlexFullWidth : '',
                ].join(' ')}
              >
                <SelectableTable<ExamTaskGroupSampleSolutionsTableItem, SampleAssessmentSortBy>
                  columns={columns}
                  rows={examTaskGroupSolutions}
                  onFilterChange={handleFilterChange}
                  filter={filter}
                  filterElements={filterElements}
                  isAdvancedFilterState={isAdvancedFilter}
                  onAdvancedFilterModalOpen={() => setAdvancedFilterModalOpen(true)}
                  onAdvancedFilterReset={onAdvancedFilterReset}
                  filterbar={filterState}
                  closeFilterItem={uncheckFilterItem}
                  uncheckAllFilters={uncheckAllFilters}
                  totalCount={pageInfo.totalCount}
                  isMultiChoice={true}
                  sortByProperty={sortByColumnId}
                  isDescending={isDescending}
                  setIsDescending={setIsDescending}
                  setSortByColumn={setSortByColumnId}
                  onSelectionChanged={setItemsSelection}
                  caption={caption}
                />
              </div>
            </ViewContainer>
          </div>
        </Grid>
      </Layout>
      <Prompt
        type={sendbackPrompt.type}
        open={sendbackPrompt.open}
        mainText={sendbackPrompt.mainText}
        subTexts={sendbackPrompt.subTexts}
        onConfirm={() => {
          setSendbackPrompt({ ...sendbackPrompt, open: false });
        }}
        mainActionButtonText="POWRÓT DO LISTY"
      />
      {advancedFilterModalOpen && (
        <AdvancedFilterModal
          advancedFilter={advancedFilter}
          isResetFilter={isResetAdvancedFilter}
          checkedTasks={checkedTasks}
          setCheckedTasks={setCheckedTasks}
          checkedCriterions={checkedCriterions}
          setCheckedCriterions={setCheckedCriterions}
          onAdvancedFilterReset={onAdvancedFilterReset}
          setIsResetAdvancedFilter={setIsResetAdvancedFilter}
          assessmentSessionName={assessmentSession.assessmentSessionName}
          handleAdvancedFilterChange={handleAdvancedFilterChange}
          onClose={() => setAdvancedFilterModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default SampleAssessment;
